import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import TablesStateContext from "../contexts/TablesStateContext";

type Props = {
  tableKey: string;
  itemsCount: number;
  cursor: string | undefined;
  nextCursor: string | undefined;
  setCursor: (cursor: string | undefined) => void;
};

const Pagination = ({
  tableKey,
  itemsCount,
  cursor,
  nextCursor,
  setCursor,
}: Props): JSX.Element => {
  const { getTableState, setPagination } = useContext(TablesStateContext);
  const savedPagination = getTableState(tableKey).pagination;

  const [page, setPage] = useState(savedPagination?.page ?? 0);
  const [history, setHistory] = useState<[string | undefined, number][]>(
    savedPagination?.history ?? [[undefined, 0]]
  );

  const startItem = history[page]?.[1] ?? 0;
  const endItem = startItem + itemsCount;

  // While navigating to another page the query refetches and `itemsCount` drops
  // to 0 momentarily. Freeze the last real range during that gap so the counter
  // doesn't flash "0 - N" before the new page's data arrives.
  const lastRangeRef = useRef<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });
  useEffect(() => {
    if (itemsCount > 0) {
      lastRangeRef.current = { start: startItem + 1, end: endItem };
    }
  }, [itemsCount, startItem, endItem]);

  const displayStart = itemsCount > 0 ? startItem + 1 : lastRangeRef.current.start;
  const displayEnd = itemsCount > 0 ? endItem : lastRangeRef.current.end;

  const historyKey = useMemo(() => JSON.stringify(history), [history]);

  useEffect(
    () => setPagination(tableKey, { page, history }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, historyKey, tableKey]
  );

  const prevNextCursorRef = useRef<string | undefined | null>(null);
  const prevEndItemRef = useRef<number | null>(null);

  useEffect(() => {
    if (cursor !== undefined) return;
    // The cursor is reset to undefined externally when filters change. On page 0
    // the cursor is always undefined (first page uses no cursor), so only reset
    // when we're past page 0 — otherwise this clobbers the history we build as we
    // record the next page's cursor while still on page 0.
    if (page !== 0) {
      setPage(0);
      setHistory([[undefined, 0]]);
      // Clear refs so the history-tracking effect doesn't suppress an update
      // if the next page of data happens to return the same cursor+endItem.
      prevNextCursorRef.current = null;
      prevEndItemRef.current = null;
    }
  }, [cursor, page]);

  useEffect(() => {
    if (
      // Skip if there are no items — nextCursor should also be undefined in
      // this case, but guard explicitly in case the API ever returns a cursor
      // alongside an empty page, to avoid appending a phantom history entry.
      itemsCount === 0 ||
      nextCursor === undefined ||
      (nextCursor === prevNextCursorRef.current &&
        endItem === prevEndItemRef.current)
    )
      return;
    prevNextCursorRef.current = nextCursor;
    prevEndItemRef.current = endItem;
    setHistory((prev) => {
      const next = [
        ...prev.slice(0, page + 1),
        [nextCursor, endItem] as [string | undefined, number],
      ];
      if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
      return next;
    });
  }, [nextCursor, page, endItem, itemsCount]);

  const handlePrevious = () => {
    setPage(page - 1);
    setCursor(history[page - 1]?.[0]);
  };

  const handleNext = () => {
    setPage(page + 1);
    setCursor(history[page + 1]?.[0] ?? nextCursor);
  };

  const hasNextPage = nextCursor !== undefined;
  const hasPrevPage = page > 0;

  return (
    <Flex alignItems="center" justifyContent="center" gap={2}>
      <Button variant="ghost" onClick={handlePrevious} disabled={!hasPrevPage}>
        <Box opacity={hasPrevPage ? 1 : 0.5}>
          <ChevronLeft />
        </Box>
      </Button>
      <Text>
        Registros {displayStart} - {displayEnd}
      </Text>
      <Button variant="ghost" onClick={handleNext} disabled={!hasNextPage}>
        <Box opacity={hasNextPage ? 1 : 0.5}>
          <ChevronRight />
        </Box>
      </Button>
    </Flex>
  );
};

export default Pagination;
